import { useTranslation } from "@sagot/i18n";
import { useMemo } from "react";
import { z } from "zod";
import {
  AUTH_TYPES,
  HTTP_METHODS,
  PARAM_TYPES,
  TIMEOUT_MAX_MS,
  TIMEOUT_MIN_MS,
  TOOL_NAME_PATTERN,
  WHEN_TO_USE_MAX,
  formSchema,
  mappingValid,
  rowsValid,
  urlValid,
  type ActionFormValues,
} from "./schema-model";

const NAME_MAX = 120;

/** zod rules for the editor; HTTP rules apply to custom actions only (`builtin` skips them). */
export function useActionSchema(builtin: boolean, hasSecret: boolean) {
  const { t } = useTranslation();
  return useMemo(
    () =>
      z
        .object({
          name: z.string().trim().min(1, t("build.actions.editor.nameRequired")).max(NAME_MAX),
          tool_name: z.string().trim(),
          when_to_use: z.string().trim().min(1, t("build.actions.editor.whenRequired")).max(WHEN_TO_USE_MAX, t("build.actions.editor.whenTooLong", { max: WHEN_TO_USE_MAX })),
          enabled: z.boolean(),
          requires_confirmation: z.boolean(),
          requires_identity: z.boolean(),
          audiences: z.array(z.number().int()).min(1, t("build.actions.editor.audienceRequired")),
          params: z.array(z.object({ name: z.string(), type: z.enum(PARAM_TYPES), required: z.boolean(), description: z.string() })),
          jsonMode: z.boolean(),
          schemaJson: z.string(),
          method: z.enum(HTTP_METHODS as [ActionFormValues["method"], ...ActionFormValues["method"][]]),
          url: z.string(),
          headers: z.array(z.object({ name: z.string(), value: z.string() })),
          auth_type: z.enum(AUTH_TYPES as [ActionFormValues["auth_type"], ...ActionFormValues["auth_type"][]]),
          auth_header: z.string(),
          secret: z.string(),
          timeout_ms: z.number().int().min(TIMEOUT_MIN_MS).max(TIMEOUT_MAX_MS),
          mapping: z.array(z.object({ field: z.string(), path: z.string() })),
        })
        .superRefine((values, ctx) => {
          if (builtin) return;
          if (!TOOL_NAME_PATTERN.test(values.tool_name)) ctx.addIssue({ code: "custom", path: ["tool_name"], message: t("build.actions.editor.toolNameInvalid") });
          const schema = formSchema(values);
          if (!schema) ctx.addIssue({ code: "custom", path: ["schemaJson"], message: t("build.actions.editor.jsonInvalid") });
          if (!values.jsonMode && !rowsValid(values.params)) ctx.addIssue({ code: "custom", path: ["params"], message: t("build.actions.editor.paramInvalid") });
          if (!urlValid(values.url, Object.keys(schema?.properties ?? {}))) ctx.addIssue({ code: "custom", path: ["url"], message: t("build.actions.editor.urlInvalid") });
          if (!mappingValid(values.mapping)) ctx.addIssue({ code: "custom", path: ["mapping"], message: t("build.actions.editor.mappingInvalid") });
          if (values.auth_type !== "none" && !values.secret && !hasSecret) ctx.addIssue({ code: "custom", path: ["secret"], message: t("build.actions.editor.secretRequired") });
        }),
    [t, builtin, hasSecret],
  );
}
